// src/chats.svelte.ts
import {
  CREATE_CHAT_ENDPOINT,
  AI_PREFERENCES_ENDPOINT,
  getChatMessagesUrl,
  getChatPreferencesUrl,
  getSearchChatsUrl,
  getUploadDocumentUrl,
  getPresignDocumentsUrl,
  getCompleteDocumentsUrl,
  getListDocumentsUrl,
  getDeleteDocumentUrl,
  getPinChatUrl,
  getRenameChatUrl,
  getDeleteChatUrl,
} from './externalUrls'
import { getAuthHeaders, getFileUploadHeaders } from './lib/utils'
import type { Source } from './lib/ws'
import { EFFORT_FALLBACK_OPTIONS, type EffortOption } from './lib/effort'

export type ChatItem = {
  id: string;
  title: string;
  is_pinned: boolean;
};

export type ChatGroup = {
  name: string;
  chats: ChatItem[];
};

export type ChatsResponse = {
  chats: ChatGroup[];
};

export type Attachment = {
  mimeType: string;       // e.g. 'audio/webm', 'image/jpeg', 'application/pdf'
  filename?: string;      // e.g. 'contrato.pdf'
  mediaType: 'audio' | 'image' | 'document';  // UI category
};

export type Message = {
  id: string;
  /** PK del mensaje en el backend (presente solo tras cargar del historial). */
  messageId?: number;
  role: 'user' | 'assistant' | 'status';
  text: string;
  ts: number;
  attachment?: Attachment;  // present when user sent a file/audio
  sources?: Source[];       // URLs citadas/consultadas por el agente en esta respuesta
};

// ── Búsqueda global de chats (lupa del sidebar) ──────────────
export type SearchMatch = {
  messageId: number;
  role: 'user' | 'assistant' | 'system';
  snippet: string;
  timestamp: string;
};

export type SearchChatResult = {
  chatId: string;
  title: string;
  titleMatch: boolean;
  matchCount: number;
  matches: SearchMatch[];
};

export type SearchChatsResponse = {
  query: string;
  totalMatches: number;
  chats: SearchChatResult[];
};

type RawChatMessage = {
  message_id?: number;
  role: string;
  text: string;
  timestamp?: string;
  sources?: Source[];
};

/** Normaliza el rol del backend ('human'/'ai') al vocabulario del frontend. */
function normalizeRole(role: string): 'user' | 'assistant' {
  return role === 'human' || role === 'user' ? 'user' : 'assistant';
}

function normalizeSearchRole(role: string): SearchMatch['role'] {
  if (role === 'human' || role === 'user') return 'user';
  if (role === 'system') return 'system';
  return 'assistant';
}

/**
 * Convierte un mensaje del backend en el modelo local. Cuando el backend
 * entrega el PK se usa un id estable (`srv-<id>`) para poder enfocar el
 * mensaje desde el buscador global.
 */
function toLocalMessage(raw: RawChatMessage): Message {
  const tsNum = raw.timestamp ? Date.parse(raw.timestamp) : undefined;
  const serverId =
    typeof raw.message_id === 'number' && Number.isFinite(raw.message_id)
      ? raw.message_id
      : null;
  return {
    id: serverId !== null
      ? `srv-${serverId}`
      : `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    ...(serverId !== null ? { messageId: serverId } : {}),
    role: normalizeRole(raw.role),
    text: raw.text,
    ts: tsNum ?? Date.now(),
    ...(Array.isArray(raw.sources) && raw.sources.length > 0
      ? { sources: raw.sources as Source[] }
      : {})
  };
}

type MessagesByChat = Record<string, Message[]>;

export type PendingMessage = {
  text: string;
  attachmentType: 'text' | 'audio' | 'file';
  mediaData?: string;   // Base64 (sin el prefijo data:...)
  mimeType?: string;
  filename?: string;
  files?: File[];
};

export let groups = $state({ groups: [] as ChatGroup[] });
export let messagesByChat = $state({ chats: {} as MessagesByChat });
export let pendingInitial = $state({} as Record<string, PendingMessage>);
export let unreadByChat = $state({} as Record<string, boolean>);
export let busyByChat = $state({} as Record<string, boolean>);

// ── Objetivo de scroll (buscador global) ─────────────────────
// El buscador del sidebar registra aquí el mensaje que debe enfocarse al
// abrir el chat; ChatView lo consume al terminar de cargar el historial.
export let scrollTargetByChat = $state({} as Record<string, number>);

/** Registra el mensaje a enfocar cuando ChatView abra (o ya tenga) el chat. */
export function requestScrollToMessage(chatId: string, messageId: number) {
  scrollTargetByChat[chatId] = messageId;
}

/** Consume (una sola vez) el objetivo de scroll pendiente de un chat. */
export function consumeScrollTarget(chatId: string): number | null {
  const target = scrollTargetByChat[chatId];
  if (target === undefined) return null;
  delete scrollTargetByChat[chatId];
  return target;
}

// ── Espera de análisis profundo por chat ─────────────────────
// Timestamp (ms) en que el backend avisó que un análisis profundo puede
// tardar 5–20 min ("Esto puede tardar entre 5 y 20 minutos..."). El ChatView
// lo usa para anclar el banner fijo con cronómetro desde ese instante.
// Se arma con setDeepWait (trigger real) y se limpia al terminar el turno
// (removeStatusMessages / setChatBusy(false)).
// Persiste en sessionStorage para sobrevivir a un refresh de la pestaña: el
// turno sigue corriendo en el backend y el aviso debe reaparecer con el
// tiempo real transcurrido.
const DEEP_WAIT_STORAGE_KEY = 'jurismart.deepWaitByChat';

export let deepWaitByChat = $state({} as Record<string, number>);

function persistDeepWait() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(DEEP_WAIT_STORAGE_KEY, JSON.stringify(deepWaitByChat));
  } catch {
    // sessionStorage no disponible (modo privado, cuota): el aviso sigue
    // funcionando en memoria; solo se pierde la persistencia entre refreshs.
  }
}

// Hidratación: restaura las marcas válidas si la pestaña se refrescó con un
// análisis en curso.
try {
  if (typeof sessionStorage !== 'undefined') {
    const raw = sessionStorage.getItem(DEEP_WAIT_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') {
      const now = Date.now();
      for (const [chatId, ts] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof ts === 'number' && Number.isFinite(ts) && ts > 0 && ts <= now) {
          deepWaitByChat[chatId] = ts;
        }
      }
    }
  }
} catch {
  // Storage no disponible o JSON corrupto: se arranca sin marcas.
}

// ── Fuentes (URLs) por chat ──────────────────────────────────
// Se llenan en vivo durante el streaming (evento WS `sources`) y se
// acumulan para conservar las fuentes de toda la conversación.
export let sourcesByChat = $state({} as Record<string, Source[]>);

function ensureSources(chatId: string) {
  if (!sourcesByChat[chatId]) {
    sourcesByChat[chatId] = [];
  }
}

function sourceKey(url: string): string {
  return (url || '').replace(/[#/]+$/, '').trim().toLowerCase();
}

/** Merge en vivo: agrega fuentes nuevas (dedup por URL) y promueve a citadas. */
export function addSources(chatId: string, incoming: Source[]) {
  if (!chatId || !Array.isArray(incoming) || incoming.length === 0) return;
  ensureSources(chatId);
  const current = sourcesByChat[chatId];
  const seen = new Map(current.map((s) => [sourceKey(s.url), s]));
  let changed = false;

  for (const inc of incoming) {
    if (!inc?.url) continue;
    const key = sourceKey(inc.url);
    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, inc);
      changed = true;
    } else if (inc.cited && !existing.cited) {
      seen.set(key, { ...existing, ...inc, cited: true });
      changed = true;
    }
  }
  if (changed) sourcesByChat[chatId] = [...seen.values()];
}

// ── Per‑Chat Document Vector Store ─────────────────────────────

export type ChatDocument = {
  id: number;
  filename: string;
  mime_type: string;
  purpose: 'context' | 'message';
  batch_id: string | null;
  file_size: number;
  status: 'uploading' | 'pending' | 'processing' | 'retrying' | 'completed' | 'failed';
  error_message: string | null;
  created_at: string;
};

type PresignedPost = {
  url: string;
  fields: Record<string, string>;
};

type PresignedChatDocument = ChatDocument & { upload: PresignedPost };

export type DocumentBatchUploadResult = {
  documents: ChatDocument[];
  failedFiles: string[];
};

export let documentsByChat = $state({} as Record<string, ChatDocument[]>);

/** Supported MIME types for vector‑store document upload (matches backend). */
const SUPPORTED_DOC_TYPES = [
  'application/pdf',
  'image/jpeg', 'image/jpg', 'image/png',
  'image/webp', 'image/tiff', 'image/bmp', 'image/gif',
];

export function isSupportedDocumentType(mimeType: string): boolean {
  return SUPPORTED_DOC_TYPES.includes(mimeType);
}

export const MAX_CHAT_MESSAGE_DOCUMENTS = 3;
export const MAX_CONTEXT_BATCH_DOCUMENTS = 40;
export const MAX_CHAT_DOCUMENT_SIZE = 49 * 1024 * 1024;

function ensureChatDocuments(chatId: string) {
  if (!documentsByChat[chatId]) {
    documentsByChat[chatId] = [];
  }
}

/** Upload a document for persistent vector‑store embedding. */
export async function uploadChatDocument(chatId: string, file: File): Promise<ChatDocument> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(getUploadDocumentUrl(chatId), {
    credentials: 'include',
    method: 'POST',
    headers: getFileUploadHeaders(),
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `Error al subir documento: ${res.status}`);
  }

  const doc = (await res.json()) as ChatDocument;
  ensureChatDocuments(chatId);
  documentsByChat[chatId] = [...documentsByChat[chatId], doc];
  return doc;
}

async function uploadFileToS3(file: File, target: PresignedChatDocument): Promise<void> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(target.upload.fields)) {
    formData.append(key, value);
  }
  formData.append('file', file);
  const response = await fetch(target.upload.url, { method: 'POST', body: formData });
  if (!response.ok) {
    throw new Error(`S3 rechazó ${file.name} (${response.status})`);
  }
}

async function confirmDocumentUploads(chatId: string, documentIds: number[]): Promise<Response> {
  let response: Response | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    response = await fetch(getCompleteDocumentsUrl(chatId), {
      credentials: 'include',
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ document_ids: documentIds }),
    });
    if (response.status !== 503) return response;
    await new Promise(resolve => setTimeout(resolve, 750 * (attempt + 1)));
  }
  return response!;
}

/** Direct-to-S3 batch upload with at most three simultaneous transfers. */
export async function uploadChatDocumentBatch(
  chatId: string,
  files: File[],
  purpose: 'context' | 'message',
): Promise<DocumentBatchUploadResult> {
  const maxFiles = purpose === 'message' ? MAX_CHAT_MESSAGE_DOCUMENTS : MAX_CONTEXT_BATCH_DOCUMENTS;
  if (files.length === 0 || files.length > maxFiles) {
    throw new Error(`Puedes cargar entre 1 y ${maxFiles} documentos.`);
  }
  for (const file of files) {
    if (!isSupportedDocumentType(file.type)) {
      throw new Error(`Formato de ${file.name} no soportado.`);
    }
    if (file.size <= 0 || file.size > MAX_CHAT_DOCUMENT_SIZE) {
      throw new Error(`${file.name} excede el límite de 49 MB.`);
    }
  }

  const initResponse = await fetch(getPresignDocumentsUrl(chatId), {
    credentials: 'include',
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      purpose,
      files: files.map(file => ({
        filename: file.name,
        mime_type: file.type,
        size: file.size,
      })),
    }),
  });
  if (!initResponse.ok) {
    const error = await initResponse.json().catch(() => ({ detail: `HTTP ${initResponse.status}` }));
    throw new Error(error.detail || 'No se pudo iniciar la carga.');
  }

  const batch = await initResponse.json() as { documents: PresignedChatDocument[] };
  if (purpose === 'context') {
    ensureChatDocuments(chatId);
    documentsByChat[chatId] = [...documentsByChat[chatId], ...batch.documents];
  }

  let nextIndex = 0;
  const succeeded: number[] = [];
  const failedIds: number[] = [];
  const failedFiles: string[] = [];
  async function worker() {
    while (nextIndex < files.length) {
      const index = nextIndex++;
      try {
        await uploadFileToS3(files[index], batch.documents[index]);
        succeeded.push(batch.documents[index].id);
      } catch {
        failedFiles.push(files[index].name);
        failedIds.push(batch.documents[index].id);
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(3, files.length) }, () => worker()));

  // Failed browser transfers never reached OCR. Remove their metadata so they
  // do not remain forever in an "uploading" state.
  await Promise.allSettled(failedIds.map(docId => fetch(getDeleteDocumentUrl(chatId, docId), {
    credentials: 'include',
    method: 'DELETE',
    headers: getAuthHeaders(),
  })));
  if (purpose === 'context' && failedIds.length > 0) {
    const failedSet = new Set(failedIds);
    documentsByChat[chatId] = documentsByChat[chatId].filter(doc => !failedSet.has(doc.id));
  }

  let completed: ChatDocument[] = [];
  if (succeeded.length > 0) {
    const completeResponse = await confirmDocumentUploads(chatId, succeeded);
    if (!completeResponse.ok) {
      const error = await completeResponse.json().catch(() => ({ detail: `HTTP ${completeResponse.status}` }));
      throw new Error(error.detail || 'No se pudo confirmar la carga.');
    }
    completed = ((await completeResponse.json()) as { documents: ChatDocument[] }).documents;
    if (purpose === 'context') {
      const updates = new Map(completed.map(doc => [doc.id, doc]));
      documentsByChat[chatId] = documentsByChat[chatId].map(doc => updates.get(doc.id) ?? doc);
    }
  }
  return { documents: completed, failedFiles };
}

/** Fetch the current document list for a chat. */
export async function listChatDocuments(
  chatId: string,
  purpose: 'context' | 'message' = 'context',
  documentIds: number[] = [],
): Promise<ChatDocument[]> {
  const params = new URLSearchParams({ purpose });
  if (documentIds.length > 0) params.set('ids', documentIds.join(','));
  const res = await fetch(`${getListDocumentsUrl(chatId)}?${params.toString()}`, {
    credentials: 'include',
    method: 'GET',
  });

  if (!res.ok) {
    throw new Error(`Error al listar documentos: ${res.status}`);
  }

  const data = (await res.json()) as { documents: ChatDocument[] };
  const documents = data.documents ?? [];
  if (purpose === 'context') documentsByChat[chatId] = documents;
  return documents;
}

export async function waitForChatDocuments(
  chatId: string,
  documentIds: number[],
  timeoutMs = 10 * 60 * 1000,
): Promise<ChatDocument[]> {
  const deadline = Date.now() + timeoutMs;
  const wanted = new Set(documentIds);
  while (Date.now() < deadline) {
    const docs = await listChatDocuments(chatId, 'message', documentIds);
    if (docs.length === wanted.size && docs.every(doc => doc.status === 'completed' || doc.status === 'failed')) {
      return docs;
    }
    await new Promise(resolve => setTimeout(resolve, 2500));
  }
  throw new Error('El procesamiento de los documentos superó el tiempo máximo de espera.');
}

/** Delete a document and its vector chunks. */
export async function deleteChatDocument(chatId: string, docId: number): Promise<void> {
  const res = await fetch(getDeleteDocumentUrl(chatId, docId), {
    credentials: 'include',
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!res.ok && res.status !== 204) {
    throw new Error(`Error al eliminar documento: ${res.status}`);
  }

  // Remove from local state
  ensureChatDocuments(chatId);
  documentsByChat[chatId] = documentsByChat[chatId].filter(d => d.id !== docId);
}

/** Check whether any document in the chat is still being processed. */
export function hasPendingDocuments(chatId: string): boolean {
  const docs = documentsByChat[chatId];
  if (!docs) return false;
  return docs.some(d => ['uploading', 'pending', 'processing', 'retrying'].includes(d.status));
}

// Utilidades básicas
function ensureChatMessages(chatId: string) {
  if (!messagesByChat.chats[chatId]) {
    messagesByChat.chats[chatId] = [];
  }
}

export function getMessages(chatId: string): Message[] {
  return messagesByChat.chats[chatId] ?? [];
}

export function addUserMessage(chatId: string, text: string) {
  ensureChatMessages(chatId);
  messagesByChat.chats[chatId] = [
    ...messagesByChat.chats[chatId],
    {
      id: `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      role: 'user',
      text,
      ts: Date.now()
    }
  ];
}

export function addUserAttachmentMessage(
  chatId: string,
  text: string,
  attachment: Attachment
) {
  ensureChatMessages(chatId);
  messagesByChat.chats[chatId] = [
    ...messagesByChat.chats[chatId],
    {
      id: `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      role: 'user',
      text: text || attachment.filename || 'Archivo adjunto',
      ts: Date.now(),
      attachment
    }
  ];
}

/** Replace the latest voice-note placeholder with its server transcription. */
export function updateLatestAudioTranscription(chatId: string, transcription: string) {
  ensureChatMessages(chatId);
  const messages = messagesByChat.chats[chatId];
  let targetIndex = -1;

  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    if (message.role === 'user' && message.attachment?.mediaType === 'audio') {
      targetIndex = index;
      break;
    }
  }

  if (targetIndex === -1) return;
  messagesByChat.chats[chatId] = messages.map((message, index) =>
    index === targetIndex ? { ...message, text: transcription } : message
  );
}

export function addAssistantMessage(
  chatId: string,
  text: string,
  ts?: number,
  sources?: Source[],
  messageId?: number
) {
  ensureChatMessages(chatId);
  const hasServerId = typeof messageId === 'number' && Number.isFinite(messageId);
  messagesByChat.chats[chatId] = [
    ...messagesByChat.chats[chatId],
    {
      id: hasServerId
        ? `srv-${messageId}`
        : `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      ...(hasServerId ? { messageId: messageId as number } : {}),
      role: 'assistant',
      text,
      ts: ts ?? Date.now(),
      ...(sources && sources.length > 0 ? { sources } : {})
    }
  ];
}

export function setStatusMessage(chatId: string, text: string, ts?: number) {
  ensureChatMessages(chatId);
  const current = messagesByChat.chats[chatId];
  const regularMessages = current.filter((msg) => msg.role !== 'status');
  const statuses = current.filter((msg) => msg.role === 'status');
  const latest = statuses[statuses.length - 1];

  // Repeated keepalives/reconnect notices update the current item instead of
  // creating visual spam. Distinct progress steps form a four-item timeline.
  if (latest?.text === text) {
    messagesByChat.chats[chatId] = [
      ...regularMessages,
      ...statuses.slice(0, -1),
      { ...latest, ts: ts ?? Date.now() },
    ];
    return;
  }

  // Thinking en vivo (🧠): UNA sola burbuja viva que se reemplaza a sí misma.
  // Nunca se acumula en la timeline; se limpia al llegar la respuesta (assistant).
  if (text.startsWith('🧠') && latest?.text.startsWith('🧠')) {
    messagesByChat.chats[chatId] = [
      ...regularMessages,
      ...statuses.slice(0, -1),
      { ...latest, text, ts: ts ?? Date.now() },
    ];
    return;
  }

  const nextStatus: Message = {
    id: `status-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    role: 'status',
    text,
    ts: ts ?? Date.now()
  };

  // Un milestone real (🔎 SIMIT, ⚖️ legal…) consume la burbuja de thinking:
  // ocupa su lugar en vez de apilarse dejando thinking stale en la timeline.
  const thinkingSlot = statuses.length > 0 && statuses[statuses.length - 1].text.startsWith('🧠');

  messagesByChat.chats[chatId] = [
    ...regularMessages,
    ...(thinkingSlot
      ? [...statuses.slice(0, -1), nextStatus]
      : [...statuses, nextStatus].slice(-4)),
  ];
}

export function removeStatusMessages(chatId: string) {
  ensureChatMessages(chatId);
  messagesByChat.chats[chatId] = messagesByChat.chats[chatId].filter(
    (msg) => msg.role !== 'status'
  );
  // Los statuses se limpian al cerrar el turno (respuesta/END/error): el
  // aviso de espera de análisis profundo pertenece al turno, así que cae con ellos.
  clearDeepWait(chatId);
}

// Cargar grupos desde backend
export function setGroupsFromBackend(backendGroups: ChatGroup[]) {
  groups.groups = backendGroups ?? [];
}

/** Crear (o localizar) grupo "Hoy" y añadir chat al inicio */
export function addChatToTodayGroup(chat: ChatItem) {
  let todayIndex = groups.groups.findIndex((g) => g.name === 'Hoy');
  if (todayIndex === -1) {
    // "Hoy" no existe — crearlo. Pero si hay "Fijados" al inicio,
    // insertarlo después de "Fijados", no antes.
    const fijadosIndex = groups.groups.findIndex((g) => g.name === 'Fijados');
    const insertAfter = fijadosIndex === 0 ? 1 : 0;
    const before = groups.groups.slice(0, insertAfter);
    const after = groups.groups.slice(insertAfter);
    groups.groups = [...before, { name: 'Hoy', chats: [chat] }, ...after];
  } else {
    const updatedGroup: ChatGroup = {
      ...groups.groups[todayIndex],
      chats: [chat, ...groups.groups[todayIndex].chats]
    };
    groups.groups = [
      ...groups.groups.slice(0, todayIndex),
      updatedGroup,
      ...groups.groups.slice(todayIndex + 1)
    ];
  }
}

export function touchChat(chatId: string) {
  // Find the chat in any group
  let foundGroupIndex = -1;
  let foundChatIndex = -1;
  let foundChat: ChatItem | null = null;

  for (let gi = 0; gi < groups.groups.length; gi++) {
    const g = groups.groups[gi];
    const ci = g.chats.findIndex((c) => c.id === chatId);
    if (ci !== -1) {
      foundGroupIndex = gi;
      foundChatIndex = ci;
      foundChat = g.chats[ci];
      break;
    }
  }

  if (foundGroupIndex === -1 || foundChatIndex === -1 || !foundChat) {
    return;
  }

  // If the chat is pinned, do NOT move it — it stays in "Fijados"
  if (foundChat.is_pinned) {
    return;
  }

  const group = groups.groups[foundGroupIndex];
  const newChats = [
    ...group.chats.slice(0, foundChatIndex),
    ...group.chats.slice(foundChatIndex + 1)
  ];

  const updatedGroup: ChatGroup = { ...group, chats: newChats };

  groups.groups = [
    ...groups.groups.slice(0, foundGroupIndex),
    updatedGroup,
    ...groups.groups.slice(foundGroupIndex + 1)
  ];

  addChatToTodayGroup(foundChat);
}

// --- helper unread ---
export function markChatUnread(chatId: string) {
  unreadByChat[chatId] = true;
}

export function markChatRead(chatId: string) {
  if (unreadByChat[chatId]) {
    delete unreadByChat[chatId];
  }
}

export function setChatBusy(chatId: string, value: boolean) {
  if (value) {
    busyByChat[chatId] = true;
  } else {
    if (busyByChat[chatId]) delete busyByChat[chatId];
    // Turno terminado/detenido: la espera de análisis profundo ya no aplica.
    clearDeepWait(chatId);
  }
}

/** Arma el aviso de espera de análisis profundo para un chat (trigger real).
 *  No reinicia el cronómetro si ya estaba armado en el mismo turno. */
export function setDeepWait(chatId: string) {
  if (!chatId || deepWaitByChat[chatId] !== undefined) return;
  // El cronómetro comienza cuando el navegador recibe el trigger. Así no
  // depende del reloj del servidor ni de un timestamp inválido del frame.
  deepWaitByChat[chatId] = Date.now();
  persistDeepWait();
}

/** Limpia el aviso de espera de análisis profundo de un chat. */
export function clearDeepWait(chatId: string) {
  if (deepWaitByChat[chatId] === undefined) return;
  delete deepWaitByChat[chatId];
  persistDeepWait();
}

// ── Nivel de análisis (thinking effort) por chat ─────────────
// El modelo activo y el default global se administran en el admin de Django y
// se leen en cada turno del backend, así que aquí solo se replica la
// preferencia del usuario para pintarla en el selector.
//
// `reasoning_effort` por chat: null/undefined = usar el default global.

type PreferencesPayload = {
  chat_id: string | null;
  reasoning_effort: string | null;
  effective_effort: string;
  default_effort: string;
  options: EffortOption[];
};

export let effortOptions = $state({ options: [...EFFORT_FALLBACK_OPTIONS] });
export let defaultEffort = $state({ value: 'low' });
export let reasoningEffortByChat = $state({} as Record<string, string | null>);

// Escrituras en vuelo por chat: los envíos esperan a que el PATCH aterrice,
// de modo que el mensaje use el nivel que el usuario acaba de elegir.
type EffortWrite = { token: number; promise: Promise<void> };
const effortWriteByChat = new Map<string, EffortWrite>();
const effortRevisionByChat = new Map<string, number>();
const confirmedEffortByChat = new Map<string, string | null>();
let effortWriteSeq = 0;

let aiPreferencesLoaded = false;

function applyPreferencesPayload(data: PreferencesPayload, chatId?: string) {
  if (Array.isArray(data.options) && data.options.length > 0) {
    effortOptions.options = data.options;
  }
  if (typeof data.default_effort === 'string' && data.default_effort) {
    defaultEffort.value = data.default_effort;
  }
  if (chatId) {
    reasoningEffortByChat[chatId] = data.reasoning_effort ?? null;
  }
}

/** Carga el default global y el catálogo de niveles (una vez por sesión). */
export async function loadAIPreferences(): Promise<void> {
  if (aiPreferencesLoaded) return;
  try {
    const res = await fetch(AI_PREFERENCES_ENDPOINT, { credentials: 'include' });
    if (!res.ok) return;
    applyPreferencesPayload((await res.json()) as PreferencesPayload);
    aiPreferencesLoaded = true;
  } catch {
    // Sin red: el selector sigue usable con el catálogo local.
  }
}

/** Carga la preferencia guardada de un chat. */
export async function loadChatPreferences(chatId: string): Promise<void> {
  if (!chatId) return;
  const revision = effortRevisionByChat.get(chatId);
  const hadPendingWrite = effortWriteByChat.has(chatId);
  try {
    const res = await fetch(getChatPreferencesUrl(chatId), {
      credentials: 'include',
    });
    if (!res.ok) return;
    const data = (await res.json()) as PreferencesPayload;
    if (hadPendingWrite || effortRevisionByChat.get(chatId) !== revision) return;
    applyPreferencesPayload(data, chatId);
    confirmedEffortByChat.set(chatId, data.reasoning_effort ?? null);
  } catch {
    // Silencioso: el chat conserva el último valor conocido (o el global).
  }
}

/** Nivel efectivo de un chat (su preferencia o el default global). */
export function getEffectiveEffort(chatId?: string): string {
  if (chatId) {
    const own = reasoningEffortByChat[chatId];
    if (own) return own;
  }
  return defaultEffort.value;
}

/** Catálogo de niveles para el selector. */
export function getEffortOptions(): EffortOption[] {
  return effortOptions.options;
}

/**
 * Guarda el nivel de análisis de un chat.
 *
 * Actualiza la UI de inmediato (optimista) y revierte si el backend rechaza.
 * La promesa se registra para que el envío del mensaje la espere.
 */
export function setChatReasoningEffort(
  chatId: string,
  effort: string | null,
): Promise<void> {
  if (!chatId) return Promise.resolve();

  const pending = effortWriteByChat.get(chatId);
  if (!confirmedEffortByChat.has(chatId)) {
    confirmedEffortByChat.set(chatId, reasoningEffortByChat[chatId] ?? null);
  }
  reasoningEffortByChat[chatId] = effort;

  const token = ++effortWriteSeq;
  effortRevisionByChat.set(chatId, token);
  const write = (async () => {
    // Serializar también en el servidor: ignorar respuestas viejas no basta.
    if (pending) await pending.promise.catch(() => {});
    try {
      const res = await fetch(getChatPreferencesUrl(chatId), {
        credentials: 'include',
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reasoning_effort: effort }),
      });
      if (!res.ok) {
        throw new Error(`Error al guardar el nivel de análisis: ${res.status}`);
      }
      const data = (await res.json()) as PreferencesPayload;
      confirmedEffortByChat.set(chatId, data.reasoning_effort ?? null);
      if (effortRevisionByChat.get(chatId) === token) {
        applyPreferencesPayload(data, chatId);
      }
    } catch (error) {
      if (effortRevisionByChat.get(chatId) === token) {
        reasoningEffortByChat[chatId] = confirmedEffortByChat.get(chatId) ?? null;
      }
      throw error;
    }
  })();

  effortWriteByChat.set(chatId, { token, promise: write });

  // Libera el registro solo si esta escritura sigue siendo la más reciente.
  const release = () => {
    if (effortWriteByChat.get(chatId)?.token === token) {
      effortWriteByChat.delete(chatId);
    }
  };
  void write.then(release, release);

  return write;
}

/** Espera a que termine el guardado del nivel de análisis, si hay uno en curso. */
export async function awaitPendingEffortWrite(chatId: string): Promise<void> {
  while (true) {
    const pending = effortWriteByChat.get(chatId);
    if (!pending) return;
    try {
      await pending.promise;
    } catch (error) {
      // Una elección posterior puede recuperarse de un guardado fallido.
      const latest = effortWriteByChat.get(chatId);
      if (!latest || latest.token === pending.token) throw error;
    }
  }
}

// ---------- crear chat remoto ----------
export async function createChat(
  firstMessageText: string,
  reasoningEffort?: string | null,
): Promise<{ id: string; title: string }> {
  const body: { role: string; text: string; reasoning_effort?: string } = {
    role: 'user',
    text: firstMessageText
  };
  // El nivel de análisis elegido en la pantalla de inicio se persiste con el
  // chat para que el primer turno ya lo use (sin PATCH adicional).
  if (reasoningEffort) body.reasoning_effort = reasoningEffort;

  const res = await fetch(CREATE_CHAT_ENDPOINT, {
    credentials: "include",
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body)
  });

  if (!res.ok) {
    throw new Error(`Error al crear chat: ${res.status}`);
  }

  const data = (await res.json()) as { id: string; title: string };
  const { id, title } = data;

  const chat: ChatItem = { id, title, is_pinned: false };
  addChatToTodayGroup(chat);

  // El mensaje de usuario se agrega cuando initHistory consume el pending
  // (así evitamos duplicados entre createChat y addUserAttachmentMessage)
  pendingInitial[id] = { text: firstMessageText, attachmentType: 'text' };

  return { id, title };
}

// ---------- cargar mensajes históricos ----------
export async function loadChatMessages(chatId: string) {
  // 1) caché: si ya hay mensajes en RAM, no volvemos a hacer GET
  const existing = messagesByChat.chats[chatId];
  if (existing && existing.length > 0) {
    return;
  }

  const res = await fetch(getChatMessagesUrl(chatId), {
    credentials: "include",
    method: 'GET'
  });

  if (!res.ok) {
    throw new Error(`Error al obtener mensajes del chat: ${res.status}`);
  }

  const data = (await res.json()) as {
    messages: RawChatMessage[];
  };

  // si ya había mensajes (por ejemplo, el inicial de createChat), los respetamos
  const base = messagesByChat.chats[chatId] ?? [];
  const loaded: Message[] = [...base];
  const loadedSources: Source[] = [];

  for (const m of data.messages ?? []) {
    const msg = toLocalMessage(m);
    loaded.push(msg);
    if (Array.isArray(msg.sources)) loadedSources.push(...msg.sources);
  }

  messagesByChat.chats[chatId] = loaded;
  // Fuentes del historial disponibles para la pestaña sin esperar el WS.
  if (loadedSources.length > 0) {
    addSources(chatId, loadedSources);
  }
}

/**
 * Reconstruye el historial de un chat desde el backend (fuente autoritativa).
 * Lo usa el buscador global cuando el mensaje objetivo no está en la copia en
 * RAM (p. ej. mensajes añadidos por WebSocket en esta sesión, que aún no
 * llevan el id del backend). Conserva los status transitorios y los mensajes
 * locales que todavía no fueron persistidos.
 */
export async function reloadChatMessages(chatId: string): Promise<void> {
  const res = await fetch(getChatMessagesUrl(chatId), {
    credentials: 'include',
    method: 'GET'
  });

  if (!res.ok) {
    throw new Error(`Error al obtener mensajes del chat: ${res.status}`);
  }

  const data = (await res.json()) as { messages: RawChatMessage[] };

  const previous = messagesByChat.chats[chatId] ?? [];
  const statuses = previous.filter((message) => message.role === 'status');
  const rebuilt = (data.messages ?? []).map(toLocalMessage);

  // Cola optimista: mensajes locales de usuario que aún no existen en el
  // backend. Se deduplican por texto para no repetir burbujas ya persistidas.
  const optimisticTail = previous.filter(
    (message) =>
      message.role === 'user' &&
      !message.messageId &&
      !rebuilt.some((persisted) => persisted.role === 'user' && persisted.text === message.text)
  );

  messagesByChat.chats[chatId] = [...rebuilt, ...optimisticTail, ...statuses];

  const loadedSources: Source[] = [];
  for (const message of rebuilt) {
    if (Array.isArray(message.sources)) loadedSources.push(...message.sources);
  }
  if (loadedSources.length > 0) {
    addSources(chatId, loadedSources);
  }
}

// ---------- búsqueda global de chats ----------
/**
 * Busca coincidencias de texto en todos los chats del usuario (mensajes y
 * títulos). Devuelve los resultados agrupados por chat, listos para el panel
 * de resultados del sidebar.
 */
export async function searchChats(query: string): Promise<SearchChatsResponse> {
  const res = await fetch(getSearchChatsUrl(query), {
    credentials: 'include',
    method: 'GET'
  });

  if (!res.ok) {
    throw new Error(`Error al buscar en los chats: ${res.status}`);
  }

  const data = (await res.json()) as {
    query?: string;
    total_matches?: number;
    chats?: {
      chat_id: string;
      title: string;
      title_match?: boolean;
      match_count?: number;
      matches?: {
        message_id: number;
        role: string;
        snippet?: string;
        timestamp?: string;
      }[];
    }[];
  };

  return {
    query: data.query ?? query,
    totalMatches: data.total_matches ?? 0,
    chats: (data.chats ?? []).map((chat) => ({
      chatId: chat.chat_id,
      title: chat.title,
      titleMatch: Boolean(chat.title_match),
      matchCount: chat.match_count ?? 0,
      matches: (chat.matches ?? []).map((match) => ({
        messageId: match.message_id,
        role: normalizeSearchRole(match.role),
        snippet: match.snippet ?? '',
        timestamp: match.timestamp ?? ''
      }))
    }))
  };
}

/**
 * Recovery path for a response persisted by Django but lost over WebSocket.
 * Returns true when the latest assistant response belongs to the current turn.
 */
export async function recoverLatestAssistantMessage(chatId: string): Promise<boolean> {
  const res = await fetch(getChatMessagesUrl(chatId), {
    credentials: 'include',
    method: 'GET'
  });

  if (!res.ok) return false;

  const data = (await res.json()) as {
    messages: {
      message_id?: number;
      role: 'assistant' | 'human';
      text: string;
      timestamp?: string;
      sources?: Source[];
    }[];
  };
  const latestAssistant = [...(data.messages ?? [])]
    .reverse()
    .find((message) => message.role === 'assistant');
  if (!latestAssistant) return false;

  ensureChatMessages(chatId);
  const localMessages = messagesByChat.chats[chatId];
  const alreadyPresent = localMessages.some(
    (message) => message.role === 'assistant' && message.text === latestAssistant.text
  );
  if (alreadyPresent) {
    const recoveredSources = Array.isArray(latestAssistant.sources)
      ? (latestAssistant.sources as Source[])
      : [];
    if (recoveredSources.length > 0) {
      let targetIndex = -1;
      for (let index = localMessages.length - 1; index >= 0; index--) {
        const message = localMessages[index];
        if (message.role === 'assistant' && message.text === latestAssistant.text) {
          targetIndex = index;
          break;
        }
      }
      if (targetIndex >= 0) {
        messagesByChat.chats[chatId] = localMessages.map((message, index) =>
          index === targetIndex ? { ...message, sources: recoveredSources } : message
        );
      }
      addSources(chatId, recoveredSources);
    }
    return true;
  }

  const assistantTs = latestAssistant.timestamp
    ? Date.parse(latestAssistant.timestamp)
    : Date.now();
  const latestUserTs = [...localMessages]
    .reverse()
    .find((message) => message.role === 'user')?.ts ?? 0;

  // Avoid restoring an old response from a previous turn.
  if (assistantTs + 5000 < latestUserTs) return false;

  addAssistantMessage(
    chatId,
    latestAssistant.text,
    assistantTs,
    Array.isArray(latestAssistant.sources) ? (latestAssistant.sources as Source[]) : undefined,
    latestAssistant.message_id
  );
  if (Array.isArray(latestAssistant.sources) && latestAssistant.sources.length > 0) {
    addSources(chatId, latestAssistant.sources as Source[]);
  }
  return true;
}

// ---------- pending initial ----------
export function consumePendingInitialMessage(chatId: string): PendingMessage | undefined {
  const val = pendingInitial[chatId];
  if (val !== undefined) {
    delete pendingInitial[chatId];
    return val;
  }
  return undefined;
}

// =====================================================================
// CHAT ACTIONS: PIN, RENAME, DELETE
// =====================================================================

/** Move a chat between "Fijados" and "Hoy" groups in the local state. */
function moveChatInGroups(chatId: string, toPinned: boolean): boolean {
  let foundGroupIndex = -1;
  let foundChatIndex = -1;

  for (let gi = 0; gi < groups.groups.length; gi++) {
    const g = groups.groups[gi];
    const ci = g.chats.findIndex((c) => c.id === chatId);
    if (ci !== -1) {
      foundGroupIndex = gi;
      foundChatIndex = ci;
      break;
    }
  }

  if (foundGroupIndex === -1 || foundChatIndex === -1) {
    return false;
  }

  // Remove from current group
  const group = groups.groups[foundGroupIndex];
  const chat = group.chats[foundChatIndex];
  const updatedChat: ChatItem = { ...chat, is_pinned: toPinned };

  const newChats = [
    ...group.chats.slice(0, foundChatIndex),
    ...group.chats.slice(foundChatIndex + 1),
  ];

  const updatedGroup: ChatGroup = { ...group, chats: newChats };

  groups.groups = [
    ...groups.groups.slice(0, foundGroupIndex),
    updatedGroup,
    ...groups.groups.slice(foundGroupIndex + 1),
  ];

  // Insert into the target group
  if (toPinned) {
    // Find or create "Fijados" group
    const fijadosIndex = groups.groups.findIndex((g) => g.name === 'Fijados');
    if (fijadosIndex === -1) {
      groups.groups = [
        { name: 'Fijados', chats: [updatedChat] },
        ...groups.groups,
      ];
    } else {
      const fGroup = groups.groups[fijadosIndex];
      groups.groups = [
        ...groups.groups.slice(0, fijadosIndex),
        { ...fGroup, chats: [updatedChat, ...fGroup.chats] },
        ...groups.groups.slice(fijadosIndex + 1),
      ];
    }
  } else {
    // Insert at the top of "Hoy"
    addChatToTodayGroup(updatedChat);
  }

  return true;
}

/** Update the title of a chat in the local state. */
function updateChatTitleInGroups(chatId: string, newTitle: string): boolean {
  for (let gi = 0; gi < groups.groups.length; gi++) {
    const g = groups.groups[gi];
    const ci = g.chats.findIndex((c) => c.id === chatId);
    if (ci !== -1) {
      const updatedChat: ChatItem = { ...g.chats[ci], title: newTitle };
      const updatedChats = [
        ...g.chats.slice(0, ci),
        updatedChat,
        ...g.chats.slice(ci + 1),
      ];
      groups.groups = [
        ...groups.groups.slice(0, gi),
        { ...g, chats: updatedChats },
        ...groups.groups.slice(gi + 1),
      ];
      return true;
    }
  }
  return false;
}

/** Remove a chat from all groups (after deletion). */
function removeChatFromGroups(chatId: string) {
  groups.groups = groups.groups
    .map((g) => ({
      ...g,
      chats: g.chats.filter((c) => c.id !== chatId),
    }))
    .filter((g) => g.chats.length > 0);
}

/** Check if a chat is currently pinned. */
export function isChatPinned(chatId: string): boolean {
  for (const g of groups.groups) {
    const chat = g.chats.find((c) => c.id === chatId);
    if (chat) return chat.is_pinned;
  }
  return false;
}

// ── Remote API calls ────────────────────────────────────────────

/** Pin or unpin a chat. */
export async function pinChat(chatId: string, pinned: boolean): Promise<void> {
  const res = await fetch(getPinChatUrl(chatId), {
    credentials: 'include',
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ pinned }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || 'Error al fijar chat');
  }

  moveChatInGroups(chatId, pinned);
}

/** Rename a chat. */
export async function renameChat(chatId: string, newTitle: string): Promise<void> {
  const trimmed = newTitle.trim();
  if (!trimmed) {
    throw new Error('El título no puede estar vacío.');
  }

  const res = await fetch(getRenameChatUrl(chatId), {
    credentials: 'include',
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ title: trimmed }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || 'Error al renombrar chat');
  }

  updateChatTitleInGroups(chatId, trimmed);
}

/** Delete a chat. */
export async function deleteChat(chatId: string): Promise<void> {
  const res = await fetch(getDeleteChatUrl(chatId), {
    credentials: 'include',
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || 'Error al eliminar chat');
  }

  removeChatFromGroups(chatId);
}
